import React, { useState, useEffect, useMemo } from 'react';
// --- THIS IS THE FIX: Added the missing icon imports ---
import { 
    Search, Filter, Plus, Rocket, X, Lightbulb, Pin, PinOff, Wand2, 
    Rss, Megaphone, Mail, FileText, Users 
} from 'lucide-react';
import apiService from '../../services/apiService';

// --- Configuration for Idea Types ---
const ideaTypeConfig = {
    'blog': { icon: <Rss size={16}/>, color: 'text-teal-400', name: 'Blog Post' },
    'campaign': { icon: <Megaphone size={16}/>, color: 'text-purple-400', name: 'Campaign' },
    'email': { icon: <Mail size={16}/>, color: 'text-orange-400', name: 'Email' },
    'ad': { icon: <FileText size={16}/>, color: 'text-yellow-400', name: 'Ad' },
    'social': { icon: <Users size={16}/>, color: 'text-sky-400', name: 'Social' },
    'default': { icon: <Lightbulb size={16}/>, color: 'text-slate-400', name: 'Idea' }
};

const Card = ({ children, className = '' }) => ( <div className={`bg-slate-800 border border-slate-700 rounded-lg shadow-md ${className}`}>{children}</div> );
const Button = ({ children, onClick, className = '', variant = 'primary', icon: Icon, disabled = false }) => {
    const variants = {
        primary: 'bg-brand-500 text-white hover:bg-brand-400 disabled:bg-slate-700',
        secondary: 'bg-slate-600 text-slate-100 hover:bg-slate-500',
        ghost: 'bg-transparent text-slate-300 hover:bg-slate-700 hover:text-white',
    };
    return (
        <button onClick={onClick} disabled={disabled} className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 ${variants[variant]} ${className}`}>
            {Icon && <Icon size={16} />}
            {children}
        </button>
    );
};

export default function IdeaHangarView() {
    const [ideas, setIdeas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // The component no longer needs to manage pinned state separately,
    // as the `isPinned` property will come directly from the fetched data.

    useEffect(() => {
        const fetchInitialIdeas = async () => {
            setIsLoading(true);
            try {
                // Fetch all tasks, which will include our pinned ideas
                const allTasks = await apiService.fetchFlightDeckTasks();
                const pinned = allTasks.filter(t => t.status === 'Idea').map(t => ({...t, id: t._id, isPinned: true}));
                
                // Fetch new, unpinned ideas
                const newIdeasData = await apiService.fetchNewIdeas(12);
                const key = Object.keys(newIdeasData)[0];
                const newIdeas = (newIdeasData[key] || []).map(idea => {
                    let type = 'default';
                    if (idea.title.toLowerCase().includes('blog')) type = 'blog';
                    if (idea.title.toLowerCase().includes('campaign')) type = 'campaign';
                    return { ...idea, id: idea.title, type: type, isPinned: false };
                });
                
                // Filter out new ideas that are already pinned to avoid duplicates
                const pinnedTitles = new Set(pinned.map(p => p.title));
                const uniqueNewIdeas = newIdeas.filter(idea => !pinnedTitles.has(idea.title));

                setIdeas([...pinned, ...uniqueNewIdeas]);
            } catch (error) {
                console.error("Failed to fetch ideas:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchInitialIdeas();
    }, []);

    const handlePin = async (ideaToPin) => {
        try {
            await apiService.pinIdea({
                title: ideaToPin.title,
                description: ideaToPin.description,
                origin: 'Idea Hangar',
                taskType: ideaToPin.type // Use taskType to be consistent
            });
            // Update the UI immediately to reflect the pinned state
            setIdeas(ideas.map(idea => idea.id === ideaToPin.id ? { ...idea, isPinned: true } : idea));
        } catch (error) {
            console.error("Failed to pin idea:", error);
        }
    };

    const handlePromote = async (ideaToPromote) => {
        try {
            // Create a new task in the flight deck with status 'Backlog'
            await apiService.createFlightDeckTask({
                title: ideaToPromote.title,
                description: ideaToPromote.description,
                origin: 'Idea Hangar',
                status: 'Backlog' // Promote it to the main task list
            });
            // Remove the promoted idea from the Idea Hangar view
            setIdeas(ideas.filter(idea => idea.id !== ideaToPromote.id));
        } catch (error) {
            console.error("Failed to promote idea to mission:", error);
        }
    };

    const sortedIdeas = useMemo(() => {
        return [...ideas].sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0));
    }, [ideas]);

    return (
        <div className="animate-fade-in p-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-white">Idea Hangar</h2>
                <Button variant="primary" icon={Wand2} onClick={() => { /* Logic to generate more ideas */ }}>
                    Generate New Ideas
                </Button>
            </div>

            {isLoading ? <p>Loading ideas...</p> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {sortedIdeas.map(idea => {
                        const meta = ideaTypeConfig[idea.type] || ideaTypeConfig.default;
                        return (
                            <Card key={idea.id} className={`p-4 flex flex-col justify-between group relative transition-all ${idea.isPinned ? 'border-amber-500/50' : 'hover:border-brand-500'}`}>
                                <button
                                    onClick={() => handlePin(idea)}
                                    disabled={idea.isPinned}
                                    className="absolute top-2 right-2 p-1.5 bg-slate-700/50 rounded-full text-slate-400 opacity-0 group-hover:opacity-100 disabled:opacity-100 disabled:text-amber-400 hover:bg-slate-700 hover:text-white transition-all"
                                >
                                    {idea.isPinned ? <Pin size={16} /> : <PinOff size={16} />}
                                </button>
                                
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className={meta.color}>{meta.icon}</span>
                                        <h3 className={`font-semibold ${meta.color}`}>{meta.name}</h3>
                                    </div>
                                    <p className="text-slate-200 mb-4 pr-6">{idea.description}</p>
                                </div>
                                <Button 
                                    variant="secondary" 
                                    className="w-full mt-2" 
                                    icon={Rocket} 
                                    onClick={() => handlePromote(idea)}
                                    // A pinned idea should be managed in Mission Control, not promoted again.
                                    disabled={idea.isPinned} 
                                >
                                    {idea.isPinned ? 'Pinned' : 'Promote to Mission'}
                                </Button>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}